/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	ChangeDetectorRef,
	Directive,
	Input
} from '@angular/core';
import {
	CustomActionMenuDirective
} from '@entity/directives/custom-action-menu.directive';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	ObjectArrayHelper
} from '@shared/helpers/object-array.helper';
import {
	IDropdownOption
} from '@shared/interfaces/application-objects/dropdown-option.interface';
import {
	SiteLayoutService
} from '@shared/services/site-layout.service';
import {
	isArray
} from 'lodash-es';
import {
	Observable
} from 'rxjs';

@Directive({
	selector: '[ExtendedCustomControl]'
})

/**
 * A directive representing shared logic for a formly custom control.
 *
 * @export
 * @extends {CustomActionMenuDirective}
 * @class SelectCustomControlDirective
 */
export class SelectCustomControlDirective
	extends CustomActionMenuDirective
{
	/** Initializes a new instance of the SelectCustomControlDirective.
	 *
	 * @param {SiteLayoutService} siteLayoutService
	 * The site layout service.
	 * @param {ChangeDetectorRef} changeDetector
	 * The change detector reference for this component.
	 * @memberof SelectCustomControlDirective
	 */
	 public constructor(
		public siteLayoutService: SiteLayoutService,
		public changeDetector: ChangeDetectorRef)
	{
		super(siteLayoutService,
			changeDetector);
	}

	/**
	 * Gets or sets the overload that will allow data options to be sent in.
	 * If this is not sent, the template options value will be used.
	 *
	 * @type {any[] | Observable<any[]>}
	 * @memberof SelectCustomControlDirective
	 */
	@Input() public dataOptions: any[] | Observable<any[]>;

	/**
	 * Gets or sets loading state of the field.
	 *
	 * @type {boolean}
	 * @memberof SelectCustomControlDirective
	 */
	public loading: boolean = true;

	/**
	 * Gets or sets the initial load flag.
	 *
	 * @type {boolean}
	 * @memberof SelectCustomControlDirective
	 */
	public initialLoad: boolean = true;

	/**
	 * Gets or sets the initial property disable value prior to
	 * in code alters. This value is reset on destroy.
	 *
	 * @type {boolean}
	 * @memberof SelectCustomControlDirective
	 */
	public archivedDisable: boolean;

	/**
	 * Gets or sets the initial property disable value prior to
	 * in code alters. This value is reset on destroy.
	 *
	 * @type {string}
	 * @memberof SelectCustomControlDirective
	 */
	public archivedPlaceHolder: string;

	/**
	 * Updates the formly data options based on the most current from
	 * field template options.
	 *
	 * @memberof SelectCustomControlDirective
	 */
	public updateDataOptions(): void
	{
		const modifiedValueOptions: IDropdownOption[] =
			<IDropdownOption[]>
			ObjectArrayHelper.getObjectArrayDifferences(
				<IDropdownOption[]>this.dataOptions,
				<IDropdownOption[]>this.field.props.options,
				'value');

		const modifiedLabelOptions: IDropdownOption[] =
			<IDropdownOption[]>
			ObjectArrayHelper.getObjectArrayDifferences(
				<IDropdownOption[]>this.dataOptions,
				<IDropdownOption[]>this.field.props.options,
				'label');

		if (modifiedValueOptions.length < 1
			&& modifiedLabelOptions.length < 1)
		{
			return;
		}

		this.loading = true;

		setTimeout(
			() =>
			{
				let updatedSelection: string[] | string = null;

				if (this.field.type ===
					FormlyConstants.customControls.customMultiSelect)
				{
					updatedSelection = <string[]>this.formControl.value
						?.filter((value: string) =>
							!modifiedValueOptions.find(
								(modifiedDataOption) =>
									modifiedDataOption.value === value));
				}
				else if (modifiedValueOptions.some(
					(modifiedDataOption) => modifiedDataOption.value
						=== this.formControl.value) === false)
				{
					updatedSelection = this.formControl.value;
				}

				if (this.initialLoad === false
					&& this.loading === true
					&& ((this.field.type ===
						FormlyConstants.customControls.customMultiSelect
							&& updatedSelection?.length
								!== this.formControl.value?.length)
						|| AnyHelper.isNull(updatedSelection)))
				{
					this.formControl.setValue(updatedSelection);
					this.formControl.markAsDirty();
				}

				this.dataOptions = this.field.props.options;
				super.ngOnInit();
				this.disableSelectionLogic();
				this.initialLoad = false;
				this.loading = false;
			});
	}

	/**
	 * Disables or enables the select field based on
	 * specific criteria.
	 *
	 * @memberof SelectCustomControlDirective
	 */
	public disableSelectionLogic(): void
	{
		setTimeout(
			() =>
			{
				if (isArray(this.dataOptions) === true
					&& (<any[]>this.dataOptions)?.length === 0
					|| ((<any[]>this.dataOptions)?.length === 1
						&& this.field.type !==
							FormlyConstants.customControls.customMultiSelect)
					&& this.field.props?.disableAutoSetValue !== true)
				{
					const nullValue: any =
						this.field.type ===
							FormlyConstants.customControls.customMultiSelect
							? []
							: null;

					this.formControl
						.setValue((<any[]>this.dataOptions).length > 0
								&& !AnyHelper.isNullOrEmpty(
									this.dataOptions[0].value)
							? this.dataOptions[0].value
							: nullValue);

					if (AnyHelper.isFunction(this.field.props.change))
					{
						this.field.props.change(this.field);
					}

					this.field.props.placeholder =
							AnyHelper.isNull(this.formControl.value)
								? AppConstants.placeholders.noAvailableOptions
								: this.archivedPlaceHolder;

					this.field.props.disabled = true;
				}
				else
				{
					this.field.props.disabled = this.archivedDisable;
					this.field.props.placeholder = this.archivedPlaceHolder;
				}
			},
			AppConstants.time.fiftyMilliseconds);
	}
}